import React, { useState, useEffect } from 'react';
import './ReusableTable.css';

const ReusableTable = ({
  title = 'Table',
  data = [],
  columns = [],
  initialEntriesPerPage = 5,
  searchPlaceholder = 'Search...',
  showSearch = true,
  showEntriesSelector = true,
  showPagination = true,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [entriesPerPage, setEntriesPerPage] = useState(initialEntriesPerPage);
  const [currentPage, setCurrentPage] = useState(1);

  const filteredData = data.filter(item =>
    Object.keys(item).some(key =>
      typeof item[key] === 'string' &&
      item[key].toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const totalPages = Math.ceil(filteredData.length / entriesPerPage);
  const startIndex = (currentPage - 1) * entriesPerPage;
  const endIndex = startIndex + entriesPerPage;
  const paginatedData = filteredData.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, entriesPerPage]);

  const getPaginationItems = () => {
    const items = [];
    const maxVisiblePages = 5;
    const ellipsis = <li key={`ellipsis-${Math.random()}`} className="rt-pagination__item rt-pagination__item--ellipsis">...</li>;

    items.push(
      <li
        key={1}
        className={`rt-pagination__item ${1 === currentPage ? 'rt-pagination__item--active' : ''}`}
      >
        <button
          className="rt-pagination__link"
          onClick={() => handlePageChange(1)}
        >
          1
        </button>
      </li>
    );

    if (totalPages <= maxVisiblePages) {
      for (let i = 2; i <= totalPages; i++) {
        items.push(
          <li
            key={i}
            className={`rt-pagination__item ${i === currentPage ? 'rt-pagination__item--active' : ''}`}
          >
            <button className="rt-pagination__link" onClick={() => handlePageChange(i)}>
              {i}
            </button>
          </li>
        );
      }
    } else {
      const leftBound = Math.max(2, currentPage - 1);
      const rightBound = Math.min(totalPages - 1, currentPage + 1);

      if (leftBound > 2) items.push(ellipsis);

      for (let i = leftBound; i <= rightBound; i++) {
        items.push(
          <li
            key={i}
            className={`rt-pagination__item ${i === currentPage ? 'rt-pagination__item--active' : ''}`}
          >
            <button className="rt-pagination__link" onClick={() => handlePageChange(i)}>
              {i}
            </button>
          </li>
        );
      }

      if (rightBound < totalPages - 1) items.push(ellipsis);

      items.push(
        <li
          key={totalPages}
          className={`rt-pagination__item ${totalPages === currentPage ? 'rt-pagination__item--active' : ''}`}
        >
          <button className="rt-pagination__link" onClick={() => handlePageChange(totalPages)}>
            {totalPages}
          </button>
        </li>
      );
    }

    return items;
  };

  return (
    <div className="rt-container">
      <div className="rt-header">
        <h2 className="rt-title">{title}</h2>
        <div className="rt-controls">
          {showEntriesSelector && (
            <div className="rt-entries-select">
              <label htmlFor="rt-entriesPerPage">Show</label>
              <select
                id="rt-entriesPerPage"
                className="rt-select rt-select--sm"
                value={entriesPerPage}
                onChange={(e) => setEntriesPerPage(parseInt(e.target.value))}
              >
                {[5, 10, 25, 50, 100].map(n => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
              <span>entries</span>
            </div>
          )}

          {showSearch && (
            <div className="rt-search">
              <i className="bi bi-search rt-search__icon"></i>
              <input
                type="text"
                className="rt-search__input"
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button
                  className="rt-search__clear"
                  onClick={handleClearSearch}
                  aria-label="Clear search"
                >
                  <i className="bi bi-x"></i>
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="rt-table-container">
        <table className="rt-table">
          <thead>
            <tr>
              {columns.map((column, index) => (
                <th
                  key={column.key || column.accessor || index}
                  className="rt-table__header"
                  style={column.style || {}}
                >
                  {column.title || column.Header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedData.length > 0 ? (
              paginatedData.map((item, index) => (
                <tr key={item.id || index} className="rt-table__row">
                  {columns.map((column, colIndex) => {
                    const colKey = column.key || column.accessor || `col-${colIndex}`;
                    const cellValue = column.render
                      ? column.render(item, index)
                      : item[column.key] || item[column.accessor];
                    return (
                      <td
                        key={`${item.id || index}-${colKey}`}
                        className="rt-table__cell"
                        style={column.style || {}}
                      >
                        {cellValue}
                      </td>
                    );
                  })}
                </tr>
              ))
            ) : (
              <tr className="rt-table__row">
                <td className="rt-table__cell" colSpan={columns.length}>
                  No data found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showPagination && totalPages > 1 && (
        <div className="rt-footer">
          <div className="rt-pagination-info">
            Showing {Math.min(startIndex + 1, filteredData.length)} to {Math.min(endIndex, filteredData.length)} of {filteredData.length} entries
          </div>
          <nav aria-label="Table pagination">
            <ul className="rt-pagination">
              <li className={`rt-pagination__item ${currentPage === 1 ? 'rt-pagination__item--disabled' : ''}`}>
                <button
                  className="rt-pagination__link"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  &laquo;
                </button>
              </li>
              {getPaginationItems()}
              <li className={`rt-pagination__item ${currentPage === totalPages ? 'rt-pagination__item--disabled' : ''}`}>
                <button
                  className="rt-pagination__link"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  &raquo;
                </button>
              </li>
            </ul>
          </nav>
        </div>
      )}
    </div>
  );
};

export default ReusableTable;
